'use client'

// Checkpoint 8, Slice C live-acceptance correction — the app-wide error
// boundary. Next.js's App Router places this between the root layout (which
// does no async/DB work and cannot itself throw — see layout.tsx) and every
// route below it, so this single file catches an unexpected error thrown
// anywhere in the app: a page's Server Component render, a Server Action
// invoked from within any page, or a client-side navigation's RSC fetch.
// One centralized boundary rather than per-page try/catch, per Checkpoint 8's
// own architecture discipline.
//
// Root-caused defect this fixes (see ClaudeOutputs/
// Checkpoint_08_Inventory_Image_Storage_Upload.md, "Slice C Live Acceptance
// Corrections — Idle Session Defect" for the full writeup): after the DEV
// Fabric SQL Database sat idle, its first reconnect attempt legitimately
// timed out (a transient infrastructure condition, not an auth/session
// problem — resolveSession()/getVerifiedTrustedIdentity() never throw for
// any real auth-expiry case; they already resolve to UNAUTHENTICATED
// gracefully). With no error boundary anywhere in the app, that thrown
// NayrivaDatabaseError reached Next.js's own default handling and rendered
// as a raw stack trace instead of a controlled Nayriva-branded message.
//
// This boundary deliberately does NOT try to distinguish WHY the error
// happened (SQL, Azure, a genuine programming defect) — by the time an error
// reaches here, every legitimate auth/session/context condition has already
// been resolved gracefully elsewhere (see getOnboardingState() and every
// page's own status switch) without ever throwing. Anything that does reach
// this boundary is, by construction, unexpected — so it gets one uniform,
// safe, retry-oriented treatment rather than the browser being handed a
// reason to distinguish between infrastructure classes.
//
// error.message is deliberately never rendered — NayrivaDatabaseError's own
// message is already sanitized (src/lib/db/errors.ts), but this boundary
// does not couple its UI to that contract, and a non-database error could
// carry an unsanitized message. Server-side diagnostics are unaffected by
// this component at all: Next.js's own server-side request logging (visible
// in the dev/production server console) and pool.ts's existing
// `pool.on('error', ...)` handler already capture full diagnostic detail
// independently of what the browser ever sees.

import { useEffect } from 'react'
import Link from 'next/link'

export default function GlobalErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Browser-console diagnostic only (matches the existing convention of
    // never logging raw SQL/credential detail) — the server-side log
    // already has the full error via Next.js's own request logging.
    console.error('Nayriva encountered an unexpected error:', error.digest ?? error.message)
  }, [error])

  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 440,
          width: '100%',
          background: 'var(--nayriva-surface)',
          border: '1px solid var(--nayriva-border)',
          borderRadius: 16,
          padding: '36px 32px',
          boxShadow: '0 24px 60px -40px rgba(15,36,64,.45)',
          textAlign: 'center',
        }}
      >
        <span
          role="img"
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: 'var(--nayriva-tinted-surface)',
            border: '1px solid var(--nayriva-border)',
            color: 'var(--nayriva-error)',
            fontFamily: 'var(--font-serif)',
            fontSize: 22,
            marginBottom: 18,
          }}
        >
          !
        </span>
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontWeight: 400,
            fontSize: 26,
            color: 'var(--nayriva-deep-text)',
            marginBottom: 10,
          }}
        >
          We couldn&apos;t load this page
        </h1>
        <p
          style={{
            fontSize: 15,
            color: 'var(--nayriva-body-text)',
            marginBottom: 28,
            lineHeight: 1.5,
          }}
        >
          Something interrupted the connection. Please try again — if this keeps happening, contact support.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => reset()}
            style={{
              minHeight: 52,
              padding: '0 24px',
              borderRadius: 10,
              border: 'none',
              background: 'var(--nayriva-navy)',
              color: 'var(--nayriva-cream)',
              fontFamily: 'var(--font-ui)',
              fontWeight: 600,
              fontSize: 15,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
          <Link
            href="/dashboard"
            style={{
              minHeight: 52,
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0 24px',
              borderRadius: 10,
              border: '1px solid var(--nayriva-control-border)',
              background: 'transparent',
              color: 'var(--nayriva-deep-text)',
              fontFamily: 'var(--font-ui)',
              fontWeight: 600,
              fontSize: 15,
              textDecoration: 'none',
            }}
          >
            Go to dashboard
          </Link>
        </div>
      </div>
    </div>
  )
}
